import { usePageDataStore } from '@/features/product-editor/pageStore';
import { useMyStore } from '@/queries/useStoreData';
import { useEffect, useMemo, useState } from 'react';
import { ServerPageData } from '@/features/product-editor/types';
import { useUpsertDraftPage, useUpsertPage } from '@/queries/usePageMutations';
import { StanButton, StanTab, StanTabContent, StanTabList, StanTabs } from '@/components';
import StoreSection from '@/features/product-editor/components/StoreSection';
import ProductSection from '@/features/product-editor/components/ProductSection';
import { useValidator, ValidationProvider } from '@/features/product-editor/validations/ValidationProvider';
import { stanNotify } from '@/lib';
import { EDITOR_TABS, type EditorTabKeys } from '../constants/PageEditor';

interface PageEditorProps {
    storeId: string;
    pageId?: string;
    pageType: string;
}

const PageEditorContent = ({ storeId, pageId, pageType }: PageEditorProps) => {
    const { data: storeResponse } = useMyStore();
    const { getServerPageData, loadFromServerPageData, initializePageType } = usePageDataStore();
    const { validate, reset: resetValidator } = useValidator();

    const [pageData, setPageData] = useState<ServerPageData | null>(null);
    const { mutate: upsertPageMutation } = useUpsertPage(Number(storeId));
    const { mutate: upsertDraftPageMutation } = useUpsertDraftPage(Number(storeId));

    // TABS
    // TODO: PAVI - Determine Location
    const location = 'store';
    const [activeTab, setActiveTab] = useState<string>('');
    const handleTabChange = (tab: string) => {
        setActiveTab(tab);
    };

    const tabsToShow: EditorTabKeys[] = useMemo(() => {
        const tabs = (Object.keys(EDITOR_TABS) as EditorTabKeys[]).filter(key => {
            const tab = EDITOR_TABS[key];
            if (tab?.productType) {
                return tab.productType === pageType && tab.location.includes(location);
            }
            return tab?.location.includes(location);
        });
        return tabs;
    }, [location]);

    useEffect(() => {
        if (tabsToShow.length > 0 && tabsToShow[0]) {
            setActiveTab(tabsToShow[0]);
        }
    }, [tabsToShow]);

    useEffect(() => {
        if (storeResponse && storeResponse.length > 0) {
            const linksite = storeResponse.find(store => store.store_id === Number(storeId));
            const page = linksite?.pages.find(page => page.page_id === Number(pageId));
            setPageData(page?.data || null);
        }
    }, [storeResponse, storeId, pageId]);

    useEffect(() => {
        if (pageType && !pageId) {
            initializePageType(pageType);
            resetValidator();
        }
    }, [pageType, pageId, initializePageType]);

    useEffect(() => {
        if (pageData) {
            loadFromServerPageData(pageData, Number(pageId));
            resetValidator();
        }
    }, [pageData, loadFromServerPageData]);

    const savePageData = () => {
        validate().then(valid => {
            if (valid) {
                upsertPageMutation(getServerPageData());
            } else {
                stanNotify({
                    type: 'error',
                    title: 'Validation Error',
                    text: 'Please fix the validation errors before saving',
                });
            }
        });
    };

    const saveDraftPageData = () => {
        upsertDraftPageMutation(getServerPageData());
    };

    return (
        <div>
            <StanTabs value={activeTab} onValueChange={handleTabChange}>
                <StanTabList className='mb-6'>
                    {tabsToShow?.map((tab: EditorTabKeys) => (
                        <StanTab key={tab} value={tab}>
                            {EDITOR_TABS[tab].label}
                        </StanTab>
                    ))}
                </StanTabList>

                <StanTabContent value={EDITOR_TABS.STORE_PRESENTATION.id}>
                    <StoreSection />
                </StanTabContent>

                <StanTabContent value={EDITOR_TABS.PRODUCT.id}>
                    <ProductSection />
                </StanTabContent>

                <StanTabContent value={EDITOR_TABS.COURSE.id}>
                    <h1>Course Coming Soon</h1>
                </StanTabContent>

                <StanTabContent value={EDITOR_TABS.MEETING.id}>
                    <h1>Coming Soon</h1>
                </StanTabContent>

                <StanTabContent value={EDITOR_TABS.WEBINAR.id}>
                    <h1>Webinar Coming Soon</h1>
                </StanTabContent>

                <StanTabContent value={EDITOR_TABS.OPTIONS.id}>
                    <h1>Options Coming Soon</h1>
                </StanTabContent>
            </StanTabs>

            <div className='flex gap-2'>
                <StanButton name='savePageData' onClick={saveDraftPageData} variant='outline'>
                    Save As Draft
                </StanButton>

                <StanButton name='savePageData' onClick={savePageData}>
                    Save
                </StanButton>
            </div>
        </div>
    );
};

export const PageEditor = (props: PageEditorProps) => {
    return (
        <ValidationProvider>
            <PageEditorContent {...props} />
        </ValidationProvider>
    );
};
